import type { SampleFolderMapping } from "../../../../../models/audit/evidenceUpload";
import type { SourceRow } from "../../../../../models/audit/auditSession";
import type { FolderInfo } from "./EvidenceUploadModal";
import { FolderMappingRow } from "./FolderMappingRow";

interface FolderMappingListProps {
  folders: FolderInfo[];
  samples: SourceRow[];
  /**
   * Folder path to the sample it resolves to, or null for none. Derived once
   * in the modal so the table, the file counts and the confirmed payload
   * cannot disagree about which folders are mapped.
   */
  resolvedMappings: Map<string, string | null>;
  userMappings: Map<string, string | null>;
  serverMappings: SampleFolderMapping[];
  onMappingChange: (folderPath: string, sourceRowId: string | null) => void;
  disabled?: boolean;
  samplesLoading?: boolean;
}

export function FolderMappingList({
  folders,
  samples,
  resolvedMappings,
  userMappings,
  serverMappings,
  onMappingChange,
  disabled,
  samplesLoading,
}: FolderMappingListProps) {
  // Create a lookup map for server suggestions
  const serverMappingLookup = new Map<string, SampleFolderMapping>();
  for (const mapping of serverMappings) {
    serverMappingLookup.set(mapping.folderPath, mapping);
  }

  return (
    <div className="divide-y divide-stone-200">
      {/* Header */}
      <div className="grid grid-cols-[1fr_280px] gap-4 px-4 py-2 bg-stone-100 text-xs font-medium text-stone-600 uppercase tracking-wide sticky top-0">
        <div>Folder / Files</div>
        <div>Matched Sample</div>
      </div>

      {/* Folder rows */}
      {folders.map((folder) => {
        const serverMapping = serverMappingLookup.get(folder.path);
        const currentMapping = resolvedMappings.get(folder.path) ?? null;
        const userMapping = userMappings.get(folder.path);
        const proposedMapping =
          userMapping !== undefined
            ? userMapping
            : (serverMapping?.suggestedSourceRowId ?? null);

        // Whether this row is still explained by the server's own suggestion.
        // The confidence and the matched-on line describe THAT sample, so once
        // the auditor picks a different one, or the suggestion names a sample
        // this procedure does not own, both stop being about what is on screen
        // and are dropped rather than left to contradict the selection.
        const explainedByServer =
          currentMapping !== null &&
          currentMapping === proposedMapping &&
          currentMapping === (serverMapping?.suggestedSourceRowId ?? null);

        return (
          <FolderMappingRow
            key={folder.path}
            folder={folder}
            samples={samples}
            selectedSampleId={currentMapping}
            confidence={
              explainedByServer ? serverMapping?.confidence : undefined
            }
            matchedOn={
              explainedByServer
                ? serverMapping?.suggestedSampleIdentifier
                : undefined
            }
            samplesLoading={samplesLoading}
            onMappingChange={(sourceRowId) =>
              onMappingChange(folder.path, sourceRowId)
            }
            disabled={disabled}
          />
        );
      })}

      {folders.length === 0 && (
        <div className="px-4 py-8 text-center text-stone-500">
          No folders found in ZIP file
        </div>
      )}
    </div>
  );
}
